import type { AbilityKey, Combatant, ConditionKey, RollMode } from './types';

const advantageFromTarget = new Set<ConditionKey>([
  'blinded',
  'restrained',
  'paralyzed',
  'petrified',
  'stunned',
  'unconscious',
]);
const disadvantageFromActor = new Set<ConditionKey>(['poisoned', 'restrained', 'prone', 'blinded']);

export function hasCondition(combatant: Combatant, key: ConditionKey): boolean {
  return combatant.statuses.some((status) => status.key === key);
}

export function exhaustionLevel(combatant: Combatant): number {
  return Math.max(
    0,
    ...combatant.statuses
      .filter((status) => status.key === 'exhaustion')
      .map((status) => status.level ?? 1),
  );
}

export function isConditionImmune(combatant: Combatant, key: ConditionKey): boolean {
  return combatant.immunities.conditions.includes(key);
}

export function isIncapacitated(combatant: Combatant): boolean {
  return ['incapacitated', 'paralyzed', 'petrified', 'stunned', 'unconscious'].some((key) =>
    hasCondition(combatant, key as ConditionKey),
  );
}

export function getAttackRollMode(
  actor: Combatant,
  target: Combatant,
  override: RollMode,
): Exclude<RollMode, 'auto'> {
  if (override !== 'auto') return override;
  const advantage =
    hasCondition(actor, 'invisible') ||
    target.statuses.some((status) => advantageFromTarget.has(status.key));
  const disadvantage =
    hasCondition(target, 'invisible') ||
    actor.statuses.some((status) => disadvantageFromActor.has(status.key)) ||
    exhaustionLevel(actor) >= 3;
  if (advantage === disadvantage) return 'normal';
  return advantage ? 'advantage' : 'disadvantage';
}

export function canTarget(actor: Combatant, target: Combatant): boolean {
  return !actor.statuses.some(
    (status) => status.key === 'charmed' && status.sourceUid === target.uid,
  );
}

export function isSaveAutoFail(target: Combatant, ability: AbilityKey): boolean {
  if (ability !== 'str' && ability !== 'dex') return false;
  return ['paralyzed', 'petrified', 'stunned', 'unconscious'].some((key) =>
    hasCondition(target, key as ConditionKey),
  );
}

export function hasSaveDisadvantage(target: Combatant): boolean {
  return exhaustionLevel(target) >= 3;
}
